import { ref } from 'vue';
import { pushApi } from '@/utils/api';

// Support natif : SW + Push API présents. Ne dit rien sur la permission ni sur
// iOS (qui exige en plus l'installation en PWA — cf. isAppShell côté vue).
export const isPushSupported = 'serviceWorker' in navigator && 'PushManager' in window;

export const isSubscribed = ref(false);
export const pushLoading = ref(false);

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const base64Safe = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64Safe);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export async function refreshSubscriptionState(): Promise<void> {
  if (!isPushSupported) return;
  const registration = await navigator.serviceWorker.ready;
  const sub = await registration.pushManager.getSubscription();
  isSubscribed.value = !!sub;
}

export async function subscribeToPush(): Promise<boolean> {
  if (!isPushSupported) return false;
  pushLoading.value = true;
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    const { public_key } = await pushApi.vapidPublicKey();
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });

    const json = sub.toJSON();
    await pushApi.subscribe(json.endpoint!, {
      p256dh: json.keys!.p256dh,
      auth: json.keys!.auth,
    });
    isSubscribed.value = true;
    return true;
  } finally {
    pushLoading.value = false;
  }
}

export async function unsubscribeFromPush(): Promise<void> {
  if (!isPushSupported) return;
  pushLoading.value = true;
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    if (sub) {
      await pushApi.unsubscribe(sub.endpoint);
      await sub.unsubscribe();
    }
    isSubscribed.value = false;
  } finally {
    pushLoading.value = false;
  }
}

export function usePushNotifications() {
  return { isPushSupported, isSubscribed, pushLoading, subscribeToPush, unsubscribeFromPush, refreshSubscriptionState };
}
